// ── Destinos Tab ──────────────────────────────────────────────────────────────
import { getTrip }               from '@/store/tripState';
import { saveTrip }              from '@/services/sync.service';
import { openModal }             from '@/ui/modal';
import { toastSuccess }          from '@/ui/toast';
import { fmt, toArr, uid, g,
         sanitize, requireFields } from '@/helpers';
import type { Destino, DayTrip } from '@/types';

const _expandedDestinos = new Set<string>();

export function toggleDestinoTrips(destinoId: string): void {
  if (_expandedDestinos.has(destinoId)) _expandedDestinos.delete(destinoId);
  else _expandedDestinos.add(destinoId);
  renderDestinos();
}

export function renderDestinos(): void {
  const el  = document.getElementById('destinos-list');
  if (!el) return;
  const arr = toArr(getTrip().destinos ?? {}) as Destino[];
  arr.sort((a, b) => (a.chegada ?? '').localeCompare(b.chegada ?? ''));

  if (!arr.length) {
    el.innerHTML = '<div class="empty">Nenhum destino adicionado.</div>';
    return;
  }

  el.innerHTML = arr.map(d => {
    const dayTrips = toArr(d.dayTrips ?? {}) as DayTrip[];
    dayTrips.sort((a, b) => (a.data ?? '').localeCompare(b.data ?? ''));

    const isExpanded = _expandedDestinos.has(d.id);
    const dayTripsHtml = dayTrips.length ? `
      <div class="daytrips-section">
        <button class="daytrips-toggle" onclick="window.__toggleDestinoTrips('${d.id}')">
          <span>🗺️ Bate-volta / Day trips (${dayTrips.length})</span>
          <span class="agenda-day-chevron${isExpanded ? ' open' : ''}">▾</span>
        </button>
        ${isExpanded ? `
        <div class="daytrips-list">
          ${dayTrips.map(dt => `
            <div class="daytrip-row">
              <div class="daytrip-dot"></div>
              <div class="daytrip-body">
                <span class="daytrip-cidade">${sanitize(dt.cidade)}</span>
                ${dt.data ? `<span class="daytrip-data">${fmt(dt.data)}</span>` : ''}
                ${dt.notas ? `<span class="daytrip-notas">${sanitize(dt.notas)}</span>` : ''}
              </div>
              <div class="card-actions">
                <button class="icon-btn" onclick="window.__openDayTripModal('${d.id}','${dt.id}')">✏</button>
                <button class="icon-btn del" onclick="window.__delDayTrip('${d.id}','${dt.id}')">×</button>
              </div>
            </div>`).join('')}
          <button class="btn-sm" style="margin-top:6px"
            onclick="window.__openDayTripModal('${d.id}')">+ Adicionar passeio de um dia</button>
        </div>` : ''}
      </div>` : `
      <div class="daytrips-add">
        <button class="btn-sm" onclick="window.__openDayTripModal('${d.id}')">
          🗺️ Adicionar bate-volta / day trip
        </button>
      </div>`;

    const location = d.pais ? `${sanitize(d.cidade)}, ${sanitize(d.pais)}` : sanitize(d.cidade);
    return `
      <div class="card">
        <div style="display:flex;align-items:flex-start;justify-content:space-between;gap:8px">
          <div style="flex:1;min-width:0">
            <div class="card-title">📍 ${location}</div>
            <div class="card-sub">${fmt(d.chegada)} → ${fmt(d.saida)}</div>
            ${d.notas ? `<div class="card-sub" style="margin-top:4px">${sanitize(d.notas)}</div>` : ''}
          </div>
          <div class="card-actions">
            <button class="icon-btn" onclick="window.__openModal('destino','${d.id}')">✏</button>
            <button class="icon-btn del" onclick="window.__delItem('destinos','${d.id}')">×</button>
          </div>
        </div>
        ${dayTripsHtml}
      </div>`;
  }).join('');
}

// ── Day trip modal ────────────────────────────────────────────
export function openDayTripModal(destinoId: string, editId?: string): void {
  const trip    = getTrip();
  const destino = (trip.destinos ?? {})[destinoId] as Destino | undefined;
  if (!destino) return;

  const item = editId
    ? (toArr(destino.dayTrips ?? {}) as DayTrip[]).find(dt => dt.id === editId)
    : undefined;

  openModal(
    editId ? 'Editar passeio' : `Bate-volta a partir de ${destino.cidade}`,
    `<div class="fg" style="margin-bottom:4px">
       <div style="font-size:12px;color:var(--muted);margin-bottom:10px;padding:8px;background:var(--bg2);border-radius:var(--r)">
         Um bate-volta (day trip) é uma excursão de um dia saindo de <strong>${sanitize(destino.cidade)}</strong> e voltando no mesmo dia.
       </div>
     </div>
     <div class="fg"><label>Cidade / destino do passeio</label>
       <input id="f-dt-cidade" value="${item?.cidade ?? ''}" placeholder="Ex: Edimburgo">
     </div>
     <div class="fg"><label>Data</label>
       <input type="date" id="f-dt-data" value="${item?.data ?? ''}">
     </div>
     <div class="fg"><label>Notas (opcional)</label>
       <input id="f-dt-notas" value="${item?.notas ?? ''}" placeholder="Ex: Trem das 8h, voltar até 20h">
     </div>`,
    async () => {
      requireFields([{ id: 'f-dt-cidade', label: 'Cidade do passeio' }]);
      const id  = editId ?? uid();
      const obj: DayTrip = {
        id,
        cidade: g('f-dt-cidade'),
        data:   g('f-dt-data'),
        notas:  g('f-dt-notas'),
      };
      await saveTrip(`destinos/${destinoId}/dayTrips/${id}`, obj);
      toastSuccess('Passeio salvo!');
      renderDestinos();
    }
  );
}

export async function delDayTrip(destinoId: string, dayTripId: string): Promise<void> {
  const { confirmModal } = await import('@/ui/modal');
  const ok = await confirmModal('Remover este passeio?');
  if (!ok) return;
  await saveTrip(`destinos/${destinoId}/dayTrips/${dayTripId}`, null);
  renderDestinos();
}
